import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import DialogComponent from '../../components/widgets/dialogComponent';
import { validateEmail, validateMobileNumber, validURL } from '../../utils/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Checkbox, FormControlLabel, Stack, Typography, useTheme } from '@mui/material';
import FormProviderComponent from '../../components/form/formProviderComponent';
import TextFieldComponent from '../../components/form/textFieldComponent';
import FileUploadComponent from '../../components/form/fileUploadComponent';
import SelectComponent from '../../components/form/selectComponent';
import ButtonComponent from '../../components/form/buttonComponent';
const countryList = require('country-list');

const GetQuote = ({open, setOpen}) => {
    const theme = useTheme();
    const {t} = useTranslation();

    const [agreed, setAgreed] = useState(false);

    const [fileError, setFileError] = useState(null);
    const [requirement, setRequirement] = useState(null);

    const countries = useRef();

    useEffect(() => {
        const list = countryList.getNames();
        list.sort();

        const modifiedList = [];
        for(let i = 0; i < list.length; i++){
            modifiedList.push({label: list[i], value: list[i]});
        }

        countries.current = modifiedList;
    }, [countryList]);
    
    const [showAlert, setShowAlert] = useState({
        open: false,
        success: false
    });

    const formSchema = yup.object().shape({
        name: yup.string().required(t(`form.name.empty`)),
        phone: yup.string().test('isValidPhone', t(`form.phone.invalid`), () => {
            return values.phone.trim().length === 0 || validateMobileNumber(values.phone);
        }),
        email: yup.string().required(t(`form.email.empty`)).test('isValidEmail', t(`form.email.invalid`), () => {
            return validateEmail(values.email);
        }),
        country: yup.string().required(t(`form.country.empty`)),
        company: yup.string().test('isValidCompany', t(`form.company.invalid`), () => {
            return values.company.trim().length === 0 || validURL(values.company);
        }),
        message: yup.string().required(t(`form.message.invalid`)),
        reference: yup.string()
    });
    
    const defaultValues = {
        name: '',
        phone: '',
        email: '',
        country: '',
        company: '',
        message: '',
        reference: ''
    };
    
    const formMethods = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: defaultValues
    });
    
    const {
        reset: reset,
        handleSubmit: handleSubmit,
        formState: { isSubmitting: isSubmitting },
        watch
    } = formMethods;
    
    const values = watch();
    
    const onSubmit = handleSubmit(async(data) => {
        console.log(data);
    
        reset();
        setAgreed(false);
    
        setShowAlert({
            open: true,
            success: true
        });
    
        setTimeout(() => {
            setShowAlert({
                open: false
            });
        }, 5000);
    });

    const agreeToConditions = useCallback((event) => {
        setAgreed(event.target.checked);
    }, []);

    const resetForm = useCallback(() => {
        reset();
        setAgreed(false);
    }, []);

    return (
        <>
            <DialogComponent 
                open={open}
                setOpen={setOpen}
                title={(t('quote.title'))}
                closeCallback={resetForm}
                proceedTxt={t(`form.submit`)}
                proceedCallback={() => {}}
                proceedType="submit"
                body={
                    <>
                        <Box width='100%'>
                            <FormProviderComponent methods={formMethods} onSubmit={onSubmit}>
                                <TextFieldComponent 
                                    name='name'
                                    label={t(`form.name.label`)}
                                    fullWidth
                                    required
                                />

                                <Stack
                                    direction={{xs: 'column', md: 'row'}}
                                    justifyContent={{xs: 'center', md: 'space-between'}}
                                    alignItems='start'
                                    width='100%'
                                    spacing={{xs: 0, md: 5}}
                                >
                                    <Box width='100%'>
                                        <TextFieldComponent 
                                            name='email'
                                            label={t(`form.email.label`)}
                                            fullWidth
                                            required
                                        />
                                    </Box>
                                    
                                    <Box width='100%'>
                                        <TextFieldComponent 
                                            name='phone'
                                            label={t(`form.phone.label`)}
                                            fullWidth
                                            helper={t(`form.phone.helper`)}
                                        />
                                    </Box>
                                </Stack>

                                <Stack
                                    direction={{xs: 'column', md: 'row'}}
                                    justifyContent={{xs: 'center', md: 'space-between'}}
                                    alignItems='start'
                                    width='100%'
                                    spacing={{xs: 0, md: 5}}
                                >
                                    <Box width='100%'>
                                        <SelectComponent 
                                            name='country'
                                            label={t(`form.country.label`)}
                                            fullWidth
                                            required
                                            list={countries.current}
                                        />
                                    </Box>
                                    
                                    <Box width='100%'>
                                        <TextFieldComponent 
                                            name='company'
                                            label={t(`form.company.label`)}
                                            fullWidth
                                        />
                                    </Box>
                                </Stack>

                                <TextFieldComponent 
                                    name='message'
                                    label={(t('quote.goal'))}
                                    fullWidth
                                    required
                                    rows={5}
                                />

                                <TextFieldComponent 
                                    name='reference'
                                    label={t(`quote.references.label`)}
                                    fullWidth
                                    helper={t(`quote.references.helper`)}
                                />

                                <FileUploadComponent 
                                    label={t(`quote.documents`)}
                                    error={fileError}
                                    setError={setFileError}
                                    allowedFiles={['application/pdf']}
                                    setFile={setRequirement}
                                    selectedFile={requirement}
                                />

                                <FormControlLabel 
                                    control={
                                        <Checkbox 
                                            checked={agreed}
                                            onChange={agreeToConditions}
                                        />
                                    } 
                                    label={
                                        <Typography
                                            fontWeight={500}
                                            fontSize={{xs: 10, md: 12}}
                                            textAlign="justify"
                                            lineHeight={1.5}
                                            color={theme.palette.text.primary}
                                            pt={3}
                                        >
                                            {t('form.agreement')}
                                        </Typography>
                                    }
                                />

                                <Stack
                                    direction='row'
                                    justifyContent='end'
                                >
                                    <ButtonComponent 
                                        label={t(`form.submit`)}
                                        type="submit"
                                        sx={{
                                            mt: 5
                                        }}
                                        disabled={!agreed}
                                    />
                                </Stack>

                                {
                                    showAlert.open
                                    ?
                                    <Alert 
                                        variant="filled" 
                                        severity={showAlert.success ? "success" : "error"}
                                        sx={{
                                            mt: {xs: 2, md: 3} 
                                        }}
                                    >
                                        {showAlert.success ? t('form.response.success') : t('form.response.failed')}
                                    </Alert>
                                    :
                                    <></>
                                }
                            </FormProviderComponent>
                        </Box>
                    </>
                }
            />
        </>
    );
}

export default GetQuote;